import type { DialogShortData } from '@shared/api';
import { useRef, type FC } from 'react';
import { type Components, Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import type { VirtuosoContext } from './lib/types';
import { Scroller, DialogItem } from './ui';
import { Flex, Spin } from 'antd';

export const DIALOGS_LIST_ID = 'dialogs-list-id';

interface VirtualizedDialogsListProps {
  dialogs: DialogShortData[];
  selectedDialogId: number | null;
  isFetchingMore: boolean;
  fetchNextData: () => void;
  onSelect: (dialog: DialogShortData) => void;
}

const Footer: Components<DialogShortData, VirtuosoContext>['Footer'] = ({ context }) => {
  if (!context?.isFetchingMore) return null;
  return (
    <Flex style={{ width: '100%' }} justify="center" align="center">
      <Spin />
    </Flex>
  );
};

export const VirtualizedDialogsList: FC<VirtualizedDialogsListProps> = ({
  dialogs,
  selectedDialogId,
  isFetchingMore,
  fetchNextData,
  onSelect,
}) => {
  const virtuoso = useRef<VirtuosoHandle>(null);

  const lastDialogIndex = dialogs.length - 1;

  return (
    <Virtuoso<DialogShortData, VirtuosoContext>
      ref={virtuoso}
      id={DIALOGS_LIST_ID}
      style={{
        height: 'calc(100% - 30px)',
        width: '100%',
      }}
      data={dialogs}
      endReached={fetchNextData}
      components={{ Scroller, Footer }}
      itemContent={(_, dialog, context) => (
        <DialogItem
          dialog={dialog}
          selectedDialogId={context.selectedDialogId}
          onSelect={onSelect}
        />
      )}
      context={{
        lastDialogIndex,
        selectedDialogId,
        dialogs,
        isFetchingMore,
      }}
      overscan={600}
      defaultItemHeight={88}
      skipAnimationFrameInResizeObserver
    />
  );
};
